// linkedAddRowGroups.js
//
// Pure helpers for group.linkGroup — an opt-in mechanism that keeps two or
// more addRow groups' rows synchronized (same add/remove, same row count)
// while each group keeps rendering as its own Card and submitting its own
// independent payload array (buildPayload / buildAddRowInitial are untouched
// and stay fully per-group — see payloadTransformer.js / applyGroupValues.js).
//
// A group only participates when BOTH group.addRow is true AND group.linkGroup
// is a non-empty string shared by at least one other group. Every group that
// predates this field (i.e. every group in production today) has no
// linkGroup, so these helpers are a strict no-op for it.

const truthy = (value) => value === true || value === 1 || value === '1';

// computeLinkedSets(groups) -> Map<linkGroupKey, { leaderName, memberNames: string[] }>
//
// `groups` is expected already order-sorted (normalizeGroups sorts by `order`
// before render), so the first member encountered per key is the leader —
// Array.prototype.sort is stable, so this matches the existing order
// convention with no extra tie-break logic needed. A "set" of size 1 (nothing
// else shares that linkGroup value) is dropped — a lone linkGroup value isn't
// meaningfully linked to anything, and the group renders as if unlinked.
export function computeLinkedSets(groups = []) {
    const byKey = new Map();
    groups.forEach((group) => {
        const key = group?.linkGroup;
        if (!truthy(group?.addRow) || !key) return;
        if (!byKey.has(key)) byKey.set(key, []);
        byKey.get(key).push(group.name);
    });

    const sets = new Map();
    byKey.forEach((memberNames, key) => {
        if (memberNames.length < 2) return;
        sets.set(key, { leaderName: memberNames[0], memberNames });
    });
    return sets;
}

// findLinkedSet(linkedSets, groupName) -> { leaderName, memberNames } | null
// Cheap membership lookup against an already-computed sets Map (compute once
// per render via computeLinkedSets, look up per group here — avoids
// recomputing the whole map on every group).
export function findLinkedSet(linkedSets, groupName) {
    for (const set of linkedSets.values()) {
        if (set.memberNames.includes(groupName)) return set;
    }
    return null;
}

// padLinkedInitialValues(linkedSets, initialValuesByGroupName) -> a new
// { [groupName]: rows[] } object where every member of a linked set is padded
// with {} placeholder rows up to the set's max length. Guards against
// pre-existing data where two linked groups' stored `rows` happen to differ
// in length (undefined territory otherwise — buildAddRowInitial/
// getAddRowInitialValue compute each group's rows fully independently). A
// no-op passthrough for any group not in a real (>=2 member) linked set.
export function padLinkedInitialValues(linkedSets, initialValuesByGroupName = {}) {
    const next = { ...initialValuesByGroupName };
    linkedSets.forEach(({ memberNames }) => {
        const maxLen = Math.max(...memberNames.map((name) => (next[name] ?? []).length));
        memberNames.forEach((name) => {
            const rows = next[name] ?? [];
            if (rows.length < maxLen) {
                next[name] = [...rows, ...Array.from({ length: maxLen - rows.length }, () => ({}))];
            }
        });
    });
    return next;
}
